import {ReactNode} from 'react';

import {SelectionRange, TABLE_ROW, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {computeIsAxisGhostCell} from 'notebook/tables/store/logic/TableDimensionsLogic';
import {
  computeIsAxisDimSelected,
  computeSelectionRange,
} from 'notebook/tables/store/logic/TableSelectionLogic';
import {computeShowRowExcessLabels} from 'notebook/tables/store/logic/TableVisibilityLogic';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {
  createDeepSelector,
  createTableStoreSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * Which cell of which axis a header cell labels. This much is derived identically on both axes, so
 * both axes' models share {@link createAxisHeaderIdentitySelector} for it.
 */
export interface AxisHeaderIdentity {
  /** The axis this header cell labels. */
  dim: TableDimension;
  /** The row index when `dim` is the row axis, otherwise the column index. */
  index: number;
  /** Whether this is a placeholder cell rendered outside of the table's data bounds. */
  isGhostCell: boolean;
  /**
   * How many cells this cell's axis has: the table's row count on the row axis, its column count on
   * the column axis. The cell's context menu compares this against the table's maximum to decide
   * whether inserting the selected number of rows/columns would exceed it.
   */
  numDims: number;
}

/**
 * Where an axis cell sits in the table's affixed (frozen) column layout. Both axes have one, but
 * they arrive at it from opposite ends, so this is a shared shape rather than a shared derivation:
 * the row header is the table's leading column and is always stuck to the left edge, while a column
 * header's placement depends on how many columns are frozen and where the cell falls in the
 * rendered slice. Each axis's model derives its own.
 */
export interface AxisCellAffixedLayout {
  /** Whether the cell is stuck to the left edge of the scrolling table. */
  isAffixed: boolean;
  /**
   * How far (in px) from the left edge the cell is pinned. Only meaningful while {@link isAffixed}
   * is true; an unaffixed column still reports the offset it would sit at, which nothing reads
   * because the cell scrolls with the table instead.
   */
  affixedLeftSpacing: number;
  /**
   * Whether the cell casts a shadow over the content scrolling underneath it, which only the
   * rightmost affixed cell does, and only once the table is scrolled horizontally.
   */
  showAffixedShadow: boolean;
  /** Whether the cell renders the divider marking the last user-frozen column. */
  isLastFrozenColumn: boolean;
  /** Whether the cell renders the leading-column border treatment. */
  isFirstColumn: boolean;
}

/**
 * What every axis (header) cell has, whichever axis it labels: the base that `ColumnHeaderModel`
 * and `RowHeaderModel` extend. The two axes derive these differently — a row label comes from
 * `table.rowLabels`, a column label from its column constraint — so this is a shared shape rather
 * than a shared derivation.
 *
 * Properties belong here only while both axes genuinely have them. As the axes diverge, move
 * properties down into the per-axis model instead of widening this with values that are constant
 * on one axis.
 */
export interface AxisHeaderModel extends AxisHeaderIdentity {
  /**
   * The label rendered in the cell. Null when the axis cell has no label, in which case the cell
   * falls back to the excess label ("A" / "1").
   */
  customLabel: ReactNode;
  /** Whether the cell renders with header styling. */
  isHeaderCell: boolean;
  /**
   * Whether the cell's label is left-aligned rather than centered. Each axis has its own table-level
   * setting, so the row axis reads `isLeftAlignRowHeader` and the column axis
   * `isLeftAlignColumnHeader`.
   */
  isLeftAlign: boolean;
  /**
   * Whether the cell renders the handle that drags its row/column to a new position. A ghost cell
   * labels no row/column, so it never offers one.
   *
   * A Single Input header's drag capability is owned by `TableSingleInputGroup` rather than by the
   * table, so `TableSingleInput` overrides this through `TableAxisCellProps.showDragHandle`.
   */
  showDragHandle: boolean;
  /**
   * The axis selection range this cell falls within, or null when the cell's index is outside the
   * current selection on its axis. The dimension actions in the cell's context menu operate on this
   * range, and a cell with no range offers no context menu at all.
   */
  selectionRange: SelectionRange | null;
  /**
   * Whether the current selection covers this cell's row (row axis) or column (column axis). Unlike
   * {@link selectionRange} this does not require the whole row/column to be selected, so a plain
   * cell selection still marks the headers it sits under as selected.
   */
  isSelected: boolean;
  /**
   * Whether the selection covers the whole table, in which case the cell's context menu offers
   * "Delete table" in place of deleting the selected rows/columns.
   */
  isTableSelected: boolean;
  /** Where the cell sits in the table's affixed-column layout. */
  affixedLayout: AxisCellAffixedLayout;
  /**
   * Whether the cell's "Delete row/column[s]" context menu option is disabled. Deleting operates on
   * the selected rows/columns, so this asks whether every row/column in that selection may be
   * deleted, not just this one.
   *
   * A column asks about its own {@link selectionRange}, so an unselected column reports the option
   * disabled. A row instead asks about the table's row selection regardless of which row header this
   * is, since a locked row anywhere in the selection blocks the delete.
   */
  shouldDisableDeleteDim: boolean;
  /** Whether the cell's "Insert row/column above/left" context menu option is disabled. */
  shouldDisableInsertDimBefore: boolean;
  /** Whether the cell's "Insert row/column below/right" context menu option is disabled. */
  shouldDisableInsertDimAfter: boolean;
  /**
   * Whether the table is locked against the structural edits the cell's context menu offers, which
   * hides "Delete table" on both axes and the column lock/freeze options on the column axis.
   *
   * The two axes read this from different places, matching what `TableHeaderRow` and `TableRow`
   * passed: the column axis from the `locked` prop alone, the row axis from
   * `TableLockingModel.isTableLocked`, which is additionally true while the table has placeholder
   * rows.
   */
  isLocked: boolean;
  /**
   * Whether the table is protected against edit, as a fill-in-only entry is. This hides "Delete
   * table" on both axes, and on the column axis additionally narrows the context menu to the
   * options that cannot change the table's structure.
   */
  isProtected: boolean;
  /**
   * Whether the cell renders the spreadsheet coordinate ("A" / "1") alongside {@link customLabel}.
   * Turned on while a formula is being edited. Sourced from the store's `spreadsheetState` rather
   * than from the table model, so that a formula interaction only re-renders the axis cells.
   */
  showExcessLabels: boolean;
  /**
   * Whether this cell can toggle its axis's header. Only the first cell of an axis offers the
   * toggle, and only when dimension edits are allowed and the legacy header toggle is not disabled.
   */
  canToggleHeader: boolean;
  /** Whether rows/columns can be inserted before or after this one. */
  canInsertDims: boolean;
  /** Whether rows/columns can be deleted. */
  canDeleteDims: boolean;
  /** Whether the whole table can be deleted. */
  canDeleteTable: boolean;
}

/**
 * Creates a selector for the {@link AxisHeaderModel.showExcessLabels} of an axis cell on `dim`. The
 * column axis follows the spreadsheet state on its own; the row axis additionally requires the
 * table to opt into row excess labels.
 */
export function createAxisShowExcessLabelsSelector(
  dim: TableDimension
): (store: TableEditableStore) => boolean {
  return (store) => {
    const {showExcessLabels} = store.spreadsheetState;
    if (dim !== TABLE_ROW) {
      return showExcessLabels;
    }
    // The store holds TableEditable's raw props, so this repeats the default that
    // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
    return computeShowRowExcessLabels(store.stableProps.showTableRowExcessLabels ?? true, showExcessLabels);
  };
}

/**
 * Creates a selector for the {@link AxisHeaderModel.isProtected} of an axis cell. Both axes read
 * the same table-level prop, which is what `TableRow` and `TableHeaderRow` each passed down.
 */
export function createAxisIsProtectedSelector(
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => boolean {
  // The store holds TableEditable's raw props, so this repeats the default that
  // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
  return (store) => tableSelector(store).props.isProtected ?? false;
}

/** The affixed layout of an axis cell that is laid out against a table with nothing frozen. */
export const UNAFFIXED_LAYOUT: AxisCellAffixedLayout = {
  isAffixed: false,
  affixedLeftSpacing: 0,
  showAffixedShadow: false,
  isLastFrozenColumn: false,
  isFirstColumn: false,
};

/**
 * Creates a selector for the {@link AxisHeaderIdentity} of the axis cell at (`dim`, `index`).
 * Shared by both axes' model selectors.
 */
export function createAxisHeaderIdentitySelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => AxisHeaderIdentity {
  return createTableStoreSelector(
    'axisHeaderIdentity',
    {
      rowCount: (store) => tableSelector(store).dimensions.rowCount,
      columnCount: (store) => tableSelector(store).dimensions.columnCount,
    },
    ({rowCount, columnCount}): AxisHeaderIdentity => ({
      dim,
      index,
      isGhostCell: computeIsAxisGhostCell(dim, index, columnCount),
      numDims: dim === TABLE_ROW ? rowCount : columnCount,
    })
  );
}

/**
 * Creates a selector for the {@link AxisHeaderModel.isSelected} of the axis cell at (`dim`,
 * `index`), from the merge-expanded selection box.
 *
 * The row axis additionally requires an active cell: the row header used to take its selected state
 * from `RowModel`'s per-row selection, which is only derived while the selection has an active cell.
 */
export function createAxisIsSelectedSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => boolean {
  return (store) => {
    const tableModel = tableSelector(store);
    if (!tableModel.selection.hasMultiSelection) {
      return false;
    }
    if (dim === TABLE_ROW && tableModel.state.multiSelIndex == null) {
      return false;
    }
    return computeIsAxisDimSelected(dim, index, tableModel.selection.expandedBox);
  };
}

/**
 * Creates a selector for the {@link AxisHeaderModel.isTableSelected} of an axis cell on `dim`. Each
 * axis reads it from its own end of the selection: a full row selection already spans every column
 * (and vice versa), so covering every cell of one axis means the whole table is selected.
 */
export function createAxisIsTableSelectedSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension
): (store: TableEditableStore) => boolean {
  return (store) => {
    const {selection} = tableSelector(store);
    return dim === TABLE_ROW ? selection.isEveryRowSelected : selection.isEveryColumnSelected;
  };
}

/**
 * Creates a selector for the {@link AxisHeaderModel.selectionRange} of the axis cell at
 * (`dim`, `index`). Shared by both axes' model selectors so that neither axis derives its selection
 * range differently from the other.
 *
 * The range is built fresh on every run, so this is a deep-compared selector rather than a
 * {@link createTableStoreSelector} one: keeping the result's identity stable across equal ranges is
 * what stops a selection change elsewhere in the table from invalidating every axis cell's model.
 */
export function createAxisSelectionRangeSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => SelectionRange | null {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => tableSelector(store).selection.multiAxisDimSelection],
    (multiAxisDimSelection): SelectionRange | null => computeSelectionRange(multiAxisDimSelection, dim, index)
  );
}
